/**
 * Girar elementos de una etiqueta.
 *
 * Girar no cambia el tamaño del elemento pero sí el lugar que ocupa: una línea
 * de 80 mm puesta de costado ocupa 80 mm de alto. El editor necesita esa caja
 * para poder agarrar el elemento donde de verdad está, y el servidor la usa
 * para no dejar guardar algo que se imprimiría fuera de la etiqueta.
 *
 * La cuenta está también en `backend/src/domain/etiquetas.ts`, que es la que
 * manda: acá se repite porque el dibujo la necesita en cada cuadro y no puede
 * ir a preguntarla.
 */

export type Caja = { x: number; y: number; ancho: number; alto: number };

export const normalizarGiro = (giro: number) => ((Math.round(giro) % 360) + 360) % 360;

/** El lugar que ocupa un elemento ya girado, alrededor de su centro. */
export function huellaGirada(e: { x: number; y: number; ancho: number; alto: number; giro?: number }): Caja {
  const giro = normalizarGiro(e.giro ?? 0);
  if (giro === 0 || giro === 180) return { x: e.x, y: e.y, ancho: e.ancho, alto: e.alto };

  const cx = e.x + e.ancho / 2;
  const cy = e.y + e.alto / 2;

  // Los cuartos de vuelta se resuelven de memoria: por trigonometría el seno
  // de 180° da 1e-16 en vez de cero y las medidas quedan con cola de decimales.
  const { ancho, alto } =
    giro === 90 || giro === 270
      ? { ancho: e.alto, alto: e.ancho }
      : (() => {
          const rad = (giro * Math.PI) / 180;
          const cos = Math.abs(Math.cos(rad));
          const sen = Math.abs(Math.sin(rad));
          return { ancho: e.ancho * cos + e.alto * sen, alto: e.ancho * sen + e.alto * cos };
        })();

  return { x: cx - ancho / 2, y: cy - alto / 2, ancho, alto };
}

/**
 * Lleva un desplazamiento de la etiqueta al sistema del elemento girado.
 *
 * Al estirar algo girado, el mouse se mueve en la etiqueta pero el ancho y el
 * alto son del elemento: sin esta vuelta, arrastrar hacia la derecha una línea
 * puesta de costado la haría más gruesa en vez de más larga.
 */
export function alSistemaDelElemento(dx: number, dy: number, giro: number): { dx: number; dy: number } {
  const rad = (-normalizarGiro(giro) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sen = Math.sin(rad);
  return { dx: dx * cos - dy * sen, dy: dx * sen + dy * cos };
}

/**
 * Corre un elemento lo justo para que, girado, entre en la etiqueta. Devuelve
 * la posición nueva; si ni girado entra, lo deja lo más adentro que se pueda.
 */
export function acomodarDentro(
  e: { x: number; y: number; ancho: number; alto: number; giro?: number },
  areaAncho: number,
  areaAlto: number,
): { x: number; y: number } {
  const h = huellaGirada(e);
  const corridoX = Math.min(Math.max(h.x, 0), Math.max(areaAncho - h.ancho, 0)) - h.x;
  const corridoY = Math.min(Math.max(h.y, 0), Math.max(areaAlto - h.alto, 0)) - h.y;
  return { x: e.x + corridoX, y: e.y + corridoY };
}
